import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import Ico from '../ui/Ico';

const EMPTY_ACTION = {
  type: 'send_notification', target_column_id: '', target_value: '',
  notification_message: '', delay_minutes: 0,
  email_to: '', email_subject: '', email_body: '',
  send_to_assignee: false, send_to_all_members: false,
  assign_person_user_id: '',
};

const TRIGGER_TYPES = [
  { value: 'stage_change', label: 'Etapa muda' },
  { value: 'deal_created', label: 'Negócio criado' },
  { value: 'deal_won', label: 'Negócio ganho' },
  { value: 'deal_lost', label: 'Negócio perdido' },
];

const ACTION_TYPES = [
  { value: 'send_email', label: <><Ico e="📧" />Enviar e-mail</> },
  { value: 'create_activity', label: <><Ico e="✅" />Criar atividade/tarefa</> },
  { value: 'create_note', label: <><Ico e="📝" />Criar nota</> },
  { value: 'change_stage', label: <><Ico e="🚀" />Mudar etapa</> },
  { value: 'assign_person', label: <><Ico e="👤" />Atribuir responsável</> },
  { value: 'update_field', label: <><Ico e="✏" />Atualizar campo</> },
  { value: 'send_notification', label: <><Ico e="🔔" />Enviar notificação interna</> },
];

const ACTIVITY_TYPES = [
  { value: 'task', label: 'Tarefa' },
  { value: 'call', label: 'Ligação' },
  { value: 'email', label: 'E-mail' },
  { value: 'meeting', label: 'Reunião' },
];

const NEXOSYNX_LOGO_SVG = `<svg width="44" height="44" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
  <defs><linearGradient id="ngrad" x1="0" y1="0" x2="56" y2="56" gradientUnits="userSpaceOnUse">
    <stop offset="0%" stop-color="#2563EB"/><stop offset="100%" stop-color="#00D4CB"/>
  </linearGradient></defs>
  <path d="M10 44 L10 14 L18 14 L36 36 L36 14 L44 14 L44 44 L36 44 L18 22 L18 44 Z" fill="url(#ngrad)"/>
  <circle cx="36" cy="10" r="5" fill="#00D4CB"/>
  <circle cx="28" cy="44" r="2.5" fill="#00D4CB" opacity="0.7"/>
</svg>`;

function renderEmailVars(text) {
  const sample = {
    '{{deal_title}}': 'Negócio Exemplo',
    '{{stage}}': 'Lead',
    '{{deal_value}}': 'R$ 5.000,00',
    '{{person_name}}': 'Maria Silva',
    '{{person_email}}': 'maria@exemplo.com',
    '{{organization_name}}': 'Exemplo Ltda',
    '{{probability}}': '50%',
  };
  let out = text || '';
  Object.entries(sample).forEach(([k, v]) => { out = out.split(k).join(v); });
  return out;
}

function buildEmailPreviewHtml(content) {
  const body = renderEmailVars(content).replace(/\n/g, '<br/>');
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head><meta charset="utf-8"></head>
<body style="margin:0;padding:0;background:#0B1220;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#0B1220;padding:28px 16px;">
    <tr><td align="center">
      <table role="presentation" width="640" cellpadding="0" cellspacing="0" style="max-width:640px;background:#0f172a;border-radius:16px;overflow:hidden;font-family:'Segoe UI',Arial,Helvetica,sans-serif;border:1px solid rgba(37,99,235,0.25);">
        <tr>
          <td style="background:linear-gradient(135deg,#2563EB,#00D4CB);padding:22px 32px;">
            <table role="presentation" cellpadding="0" cellspacing="0">
              <tr>
                <td valign="middle" style="padding-right:12px;">${NEXOSYNX_LOGO_SVG}</td>
                <td valign="middle">
                  <span style="color:#ffffff;font-size:20px;font-weight:700;letter-spacing:-0.02em;">NexoSynX CRM</span><br/>
                  <span style="color:rgba(255,255,255,0.85);font-size:12px;font-weight:600;letter-spacing:0.15em;text-transform:uppercase;">Gestão de Negócios</span>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="padding:28px 32px;color:#E5E7EB;font-size:15px;line-height:1.7;">${body || '<p style="color:#64748b;font-style:italic;">Digite o conteúdo do e-mail para ver a prévia aqui...</p>'}</td>
        </tr>
        <tr>
          <td style="background:#0f172a;padding:20px 32px;border-top:1px solid rgba(37,99,235,0.25);">
            <p style="margin:0;color:#94a3b8;font-size:12px;line-height:1.6;">Este e-mail foi enviado automaticamente pelo <strong style="color:#00D4CB;">NexoSynX CRM</strong>.<br/>Responda diretamente a este e-mail caso tenha dúvidas.</p>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

function AutomationForm({ pipeline, api, onSave, onCancel, initialData }) {
  const isEdit = Boolean(initialData?.id);
  const firstAction = initialData?.actions?.[0] || null;
  const [form, setForm] = useState({
    pipeline_id: initialData?.pipeline_id || pipeline?.id || '',
    name: initialData?.name || '',
    description: initialData?.description || '',
    trigger: initialData?.trigger || { type: 'stage_change', from_stage: '', to_stage: '' },
    action: firstAction ? { ...EMPTY_ACTION, ...firstAction } : { ...EMPTY_ACTION },
    active: initialData?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get('/users')
      .then(r => setUsers(r.data || []))
      .catch(() => setUsers([]));
  }, [api]);

  const stages = (pipeline?.stages || []).map(s => s.name);

  const triggerNeedsStages = form.trigger.type === 'stage_change' || form.trigger.type === 'deal_created';

  const handleSave = async () => {
    if (!form.name) { toast.error('Nome obrigatório'); return; }
    if (form.trigger.type === 'stage_change' && !form.trigger.to_stage) {
      toast.error('Selecione a etapa de destino do gatilho'); return;
    }
    setSaving(true);
    try {
      const payload = {
        pipeline_id: form.pipeline_id || null,
        name: form.name,
        description: form.description,
        trigger: form.trigger,
        actions: [{ ...form.action, delay_minutes: parseInt(form.action.delay_minutes) || 0 }],
        active: form.active,
      };
      if (isEdit) {
        await api.put(`/deals/automations/${initialData.id}`, payload);
        toast.success('Automação atualizada!');
      } else {
        await api.post('/deals/automations', payload);
        toast.success('Automação criada!');
      }
      onSave();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isEdit ? 'Erro ao atualizar automação' : 'Erro ao criar automação'));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 mt-2">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label>Nome *</Label>
          <Input className="mt-1" placeholder="Ex: Notificar quando entrar em Negociação" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </div>
        <div className="col-span-2">
          <Label>Descrição</Label>
          <Input className="mt-1" placeholder="Opcional" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>
      </div>

      <div className="border rounded-lg p-3 space-y-3 bg-blue-50/40">
        <p className="text-sm font-semibold text-gray-700"><Ico e="⚡" />Gatilho — Quando...</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Tipo</Label>
            <Select value={form.trigger.type} onValueChange={v => setForm(f => ({ ...f, trigger: { ...f.trigger, type: v } }))}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{TRIGGER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.trigger.type === 'stage_change' && (
            <>
              <div>
                <Label className="text-xs">De (vazio = qualquer)</Label>
                <Select value={form.trigger.from_stage || ''} onValueChange={v => setForm(f => ({ ...f, trigger: { ...f.trigger, from_stage: v } }))}>
                  <SelectTrigger><SelectValue placeholder="qualquer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">qualquer</SelectItem>
                    {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Para (nova etapa) *</Label>
                <Select value={form.trigger.to_stage || ''} onValueChange={v => setForm(f => ({ ...f, trigger: { ...f.trigger, to_stage: v } }))}>
                  <SelectTrigger><SelectValue placeholder="selecione a etapa" /></SelectTrigger>
                  <SelectContent>
                    {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          {form.trigger.type === 'deal_created' && (
            <div className="col-span-2">
              <Label className="text-xs">Etapa do negócio (vazio = qualquer)</Label>
              <Select value={form.trigger.from_stage || ''} onValueChange={v => setForm(f => ({ ...f, trigger: { ...f.trigger, from_stage: v } }))}>
                <SelectTrigger><SelectValue placeholder="qualquer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">qualquer</SelectItem>
                  {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {(form.trigger.type === 'deal_won' || form.trigger.type === 'deal_lost') && (
            <div className="col-span-2 text-xs text-gray-500 bg-white rounded p-2">
              Dispara quando o status do negócio mudar para {form.trigger.type === 'deal_won' ? <><Ico e="✅" />Ganho</> : <><Ico e="❌" />Perdido</>}.
            </div>
          )}
          {!triggerNeedsStages && <div className="hidden" />}
        </div>
      </div>

      <div className="border rounded-lg p-3 space-y-3 bg-green-50/40">
        <p className="text-sm font-semibold text-gray-700"><Ico e="🎯" />Ação — Então...</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Tipo de ação</Label>
            <Select value={form.action.type} onValueChange={v => setForm(f => ({ ...f, action: { ...f.action, type: v } }))}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{ACTION_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {form.action.type === 'send_email' && (<>
            <div className="col-span-2 space-y-2">
              <Label className="text-xs font-semibold">Destinatário</Label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={form.action.send_to_assignee || false}
                  onChange={e => setForm(f => ({ ...f, action: { ...f.action, send_to_assignee: e.target.checked } }))}
                  className="rounded" />
                Responsável do negócio (e-mail do usuário atribuído)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={form.action.send_to_all_members || false}
                  onChange={e => setForm(f => ({ ...f, action: { ...f.action, send_to_all_members: e.target.checked } }))}
                  className="rounded" />
                Contato do negócio (person_email)
              </label>
              <div>
                <Label className="text-xs text-gray-500">E-mail adicional (opcional)</Label>
                <Input className="mt-1" placeholder="email@exemplo.com"
                  value={form.action.email_to || ''}
                  onChange={e => setForm(f => ({ ...f, action: { ...f.action, email_to: e.target.value } }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Assunto</Label>
              <Input className="mt-1" placeholder="Atualização do negócio {{deal_title}}" value={form.action.email_subject || ''} onChange={e => setForm(f => ({ ...f, action: { ...f.action, email_subject: e.target.value } }))} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Corpo do e-mail</Label>
              <textarea
                className="mt-1 w-full min-h-[140px] p-3 rounded-lg border border-input bg-white text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={"Olá {{person_name}},\n\nO negócio \"{{deal_title}}\" mudou para a etapa {{stage}}.\n\nAcompanhe no NexoSynX CRM!"}
                value={form.action.email_body || ''}
                onChange={e => setForm(f => ({ ...f, action: { ...f.action, email_body: e.target.value } }))}
              />
            </div>
            <div className="col-span-2 text-xs text-gray-400 bg-white rounded p-2">
              Variáveis: <code>{'{{deal_title}}'}</code> · <code>{'{{stage}}'}</code> · <code>{'{{deal_value}}'}</code> · <code>{'{{person_name}}'}</code> · <code>{'{{person_email}}'}</code> · <code>{'{{organization_name}}'}</code>
            </div>
            <div className="col-span-2">
              <p className="text-xs font-semibold text-gray-700 mb-2"><Ico e="👁" />Prévia do e-mail</p>
              <div className="rounded-lg overflow-hidden border border-gray-300 bg-white">
                <iframe
                  title="Prévia do e-mail"
                  srcDoc={buildEmailPreviewHtml(form.action.email_body || '')}
                  sandbox=""
                  className="w-full h-72 bg-[#0B1220]"
                />
              </div>
            </div>
          </>)}

          {form.action.type === 'create_activity' && (<>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={form.action.target_column_id || 'task'} onValueChange={v => setForm(f => ({ ...f, action: { ...f.action, target_column_id: v } }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{ACTIVITY_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Prazo (dias a partir de hoje)</Label>
              <Input type="number" min="0" className="mt-1" placeholder="1" value={form.action.target_value || '1'} onChange={e => setForm(f => ({ ...f, action: { ...f.action, target_value: e.target.value } }))} />
            </div>
            <div>
              <Label className="text-xs">Título</Label>
              <Input className="mt-1" placeholder="Ex: Ligar para o cliente" value={form.action.email_subject || ''} onChange={e => setForm(f => ({ ...f, action: { ...f.action, email_subject: e.target.value } }))} />
            </div>
            <div>
              <Label className="text-xs">Descrição</Label>
              <Input className="mt-1" placeholder="Opcional" value={form.action.notification_message || ''} onChange={e => setForm(f => ({ ...f, action: { ...f.action, notification_message: e.target.value } }))} />
            </div>
          </>)}

          {form.action.type === 'create_note' && (
            <div className="col-span-2">
              <Label className="text-xs">Conteúdo da nota</Label>
              <Input className="mt-1" placeholder="Nota criada por automação" value={form.action.notification_message || ''} onChange={e => setForm(f => ({ ...f, action: { ...f.action, notification_message: e.target.value } }))} />
            </div>
          )}

          {form.action.type === 'change_stage' && (
            <div className="col-span-2">
              <Label className="text-xs">Etapa destino</Label>
              <Select value={form.action.target_value || ''} onValueChange={v => setForm(f => ({ ...f, action: { ...f.action, target_value: v } }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione a etapa" /></SelectTrigger>
                <SelectContent>
                  {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {form.action.type === 'assign_person' && (
            <div className="col-span-2 space-y-2">
              <Label className="text-xs font-semibold">Usuário a atribuir</Label>
              <Select value={form.action.assign_person_user_id || ''} onValueChange={v => setForm(f => ({ ...f, action: { ...f.action, assign_person_user_id: v } }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Selecionar usuário…" /></SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name || u.email || u.username || 'Usuário'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {form.action.type === 'update_field' && (<>
            <div>
              <Label className="text-xs">Campo</Label>
              <Select value={form.action.target_column_id || ''} onValueChange={v => setForm(f => ({ ...f, action: { ...f.action, target_column_id: v } }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione o campo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="probability">Probabilidade (%)</SelectItem>
                  <SelectItem value="value">Valor</SelectItem>
                  <SelectItem value="organization_name">Organização</SelectItem>
                  <SelectItem value="stage">Etapa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Valor</Label>
              <Input className="mt-1" placeholder="Novo valor" value={form.action.target_value || ''} onChange={e => setForm(f => ({ ...f, action: { ...f.action, target_value: e.target.value } }))} />
            </div>
          </>)}

          {form.action.type === 'send_notification' && (
            <div className="col-span-2">
              <Label className="text-xs">Mensagem</Label>
              <Input className="mt-1" placeholder="Texto da notificação" value={form.action.notification_message || ''} onChange={e => setForm(f => ({ ...f, action: { ...f.action, notification_message: e.target.value } }))} />
            </div>
          )}

          <div>
            <Label className="text-xs"><Ico e="⏱" />Atraso (minutos) — 0 = imediato</Label>
            <Input type="number" min="0" className="mt-1" value={form.action.delay_minutes} onChange={e => setForm(f => ({ ...f, action: { ...f.action, delay_minutes: e.target.value } }))} />
          </div>
        </div>
      </div>

      <div className="flex space-x-2 justify-end pt-2 border-t">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : isEdit ? 'Salvar Alterações' : 'Criar Automação'}</Button>
      </div>
    </div>
  );
}

export default function DealAutomations({ api, pipeline, onDataChange }) {
  const [automations, setAutomations] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);

  const fetchAutomations = async () => {
    try {
      const params = pipeline?.id ? { params: { pipeline_id: pipeline.id } } : {};
      const response = await api.get('/deals/automations', params);
      setAutomations(response.data || []);
    } catch (error) {
      console.error('Error fetching deal automations:', error);
      setAutomations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAutomations();
  }, [pipeline?.id]);

  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      const r = await api.post(`/deals/automations/${editingAutomation?.id || automations[0]?.id}/send-test-email`);
      toast.success(r.data.message || 'E-mail de teste enviado!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao enviar e-mail de teste');
    } finally {
      setSendingTest(false);
    }
  };

  const handleToggleAutomation = async (automationId, active) => {
    try {
      await api.put(`/deals/automations/${automationId}`, { active });
      setAutomations(automations.map(a => a.id === automationId ? { ...a, active } : a));
      toast.success(`Automação ${active ? 'ativada' : 'desativada'}`);
    } catch (error) {
      console.error('Error toggling automation:', error);
      toast.error('Erro ao atualizar automação');
    }
  };

  const handleDeleteAutomation = async (automationId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta automação?')) return;
    try {
      await api.delete(`/deals/automations/${automationId}`);
      setAutomations(automations.filter(a => a.id !== automationId));
      toast.success('Automação excluída!');
    } catch (error) {
      console.error('Error deleting automation:', error);
      toast.error('Erro ao excluir automação');
    }
  };

  const handleDuplicate = async (automation) => {
    try {
      await api.post('/deals/automations', {
        pipeline_id: automation.pipeline_id || pipeline?.id || null,
        name: `${automation.name} (cópia)`,
        description: automation.description,
        trigger: automation.trigger,
        actions: automation.actions,
        active: false,
      });
      toast.success('Automação duplicada!');
      fetchAutomations();
    } catch (error) {
      console.error('Error duplicating automation:', error);
      toast.error('Erro ao duplicar automação');
    }
  };

  const triggerLabel = (a) => {
    const t = a.trigger || {};
    switch (t.type) {
      case 'stage_change':
        return `Etapa muda: ${t.from_stage ? `"${t.from_stage}" → ` : ''}${t.to_stage ? `"${t.to_stage}"` : 'qualquer'}`;
      case 'deal_created':
        return `Negócio criado${t.from_stage ? ` na etapa "${t.from_stage}"` : ''}`;
      case 'deal_won':
        return 'Negócio ganho';
      case 'deal_lost':
        return 'Negócio perdido';
      default:
        return t.type || '—';
    }
  };

  const actionLabel = (a) => {
    const act = a.actions?.[0];
    if (!act) return '—';
    switch (act.type) {
      case 'send_email': return `E-mail para ${act.send_to_assignee ? 'responsável' : act.email_to || 'contato'}`;
      case 'create_activity': return `Atividade (${act.target_column_id || 'task'})`;
      case 'create_note': return "Nota";
      case 'change_stage': return `Mover para "${act.target_value}"`;
      case 'assign_person': return "Atribuir responsável";
      case 'update_field': return `${act.target_column_id}= ${act.target_value}`;
      case 'send_notification': return "Notificação interna";
      default: return act.type;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando automações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Automações de Negócios</h2>
          <p className="text-gray-600">
            Configure regras automáticas usando as etapas do pipeline
            {pipeline?.name ? ` "${pipeline.name}"` : ''}
          </p>
        </div>
        <div className="flex space-x-2">
          {automations.length > 0 && (
            <Button variant="outline" onClick={handleSendTestEmail} disabled={sendingTest} className="text-blue-600 border-blue-200 hover:bg-blue-50">
              {sendingTest ? <><Ico e="⏳" />Enviando…</> : <><Ico e="📧" />Testar E-mail</>}
            </Button>
          )}
          <Button onClick={() => setShowCreateModal(true)}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Nova Automação
          </Button>
        </div>
      </div>

      {automations.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"><Ico e="⚙" bare /></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma automação configurada</h3>
              <p className="text-gray-600 mb-6">
                Crie regras automáticas disparadas pelas etapas dos seus negócios, como enviar e-mails e criar atividades.
              </p>
              <Button onClick={() => setShowCreateModal(true)}>Criar Primeira Automação</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {automations.map((automation) => (
            <Card key={automation.id} className={`${automation.active ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50/30'}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-lg"><Ico e="⚙" bare /></div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{automation.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{automation.description}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{triggerLabel(automation)}</Badge>
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">{actionLabel(automation)}</Badge>
                        {automation.actions?.[0]?.delay_minutes > 0 && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200"><Ico e="⏱" bare />{automation.actions[0].delay_minutes}min</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={automation.active ? "default" : "secondary"}>
                      {automation.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                    <Switch checked={automation.active} onCheckedChange={(checked) => handleToggleAutomation(automation.id, checked)} />
                    <Button size="sm" variant="ghost" onClick={() => setEditingAutomation(automation)} className="text-blue-600 hover:text-blue-700" title="Editar automação">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDuplicate(automation)} className="text-gray-500 hover:text-gray-700" title="Duplicar automação">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteAutomation(automation.id)} className="text-red-600 hover:text-red-700" title="Excluir automação">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H9a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Automação</DialogTitle>
          </DialogHeader>
          <AutomationForm pipeline={pipeline} api={api} onSave={() => { setShowCreateModal(false); fetchAutomations(); onDataChange?.(); }} onCancel={() => setShowCreateModal(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editingAutomation)} onOpenChange={open => { if (!open) setEditingAutomation(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Automação</DialogTitle>
          </DialogHeader>
          {editingAutomation && (
            <AutomationForm
              pipeline={pipeline}
              api={api}
              initialData={editingAutomation}
              onSave={() => { setEditingAutomation(null); fetchAutomations(); onDataChange?.(); }}
              onCancel={() => setEditingAutomation(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
